import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import i18n from 'i18next';

// 알림 타입
type NotificationType = 'success' | 'error' | 'warning' | 'info';

// 알림 정보
// ⚠️ 규정: title과 message는 반드시 i18n 키를 사용해야 합니다.
// 예: 'connection.success', 'device.error' 등
// 메시지가 이상하면 다국어 파일(locales/)에 키를 추가하세요.
interface NotificationInfo {
  id: string;
  type: NotificationType;
  title: string; // 필수: i18n 키
  message?: string; // 선택: i18n 키
  duration?: number;
}

// 푸시 알림 정보
interface PushNotification {
  id: string;
  title?: string;
  body?: string;
  data?: Record<string, any>;
  timestamp: number;
  read: boolean;
  source: 'foreground' | 'background';
}

interface NotificationState {
  // Toast 알림
  showNotification: (notification: Omit<NotificationInfo, 'id'>) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  clearAll: () => void;

  // 푸시 알림 저장
  pushNotifications: PushNotification[];
  addPushNotification: (notification: Omit<PushNotification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearPushNotifications: () => void;
  getUnreadCount: () => number;
  getRecentNotifications: (minutes?: number) => PushNotification[];
  getNotificationsWithinMinutes: (minutes: number) => PushNotification[];
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      // Toast 알림
      // ⚠️ 규정: title과 message는 반드시 i18n 키만 전달해야 합니다.
      showNotification: ({ type, title, message, duration = 4000 }) => {
        const translatedTitle = i18n.t(title);
        const rawMessage = message ? i18n.t(message) : undefined;
        // title과 message가 동일하게 번역되면 같은 문구가 두 번 표시되므로 description을 생략
        const translatedMessage =
          rawMessage && rawMessage !== translatedTitle ? rawMessage : undefined;

        switch (type) {
          case 'success':
            toast.success(translatedTitle, {
              description: translatedMessage,
              duration,
            });
            break;
          case 'error':
            toast.error(translatedTitle, {
              description: translatedMessage,
              duration,
            });
            break;
          case 'warning':
            toast.warning(translatedTitle, {
              description: translatedMessage,
              duration,
            });
            break;
          case 'info':
            toast.info(translatedTitle, {
              description: translatedMessage,
              duration,
            });
            break;
        }
      },

      showSuccess: (title, message) => {
        get().showNotification({ type: 'success', title, message });
      },

      showError: (title, message) => {
        get().showNotification({ type: 'error', title, message });
      },

      showWarning: (title, message) => {
        get().showNotification({ type: 'warning', title, message });
      },

      showInfo: (title, message) => {
        get().showNotification({ type: 'info', title, message });
      },

      clearAll: () => {
        toast.dismiss();
      },

      // 푸시 알림 저장
      pushNotifications: [],

      addPushNotification: ({ title, body, data, source }) => {
        // ZUSTAND: 푸시 알림 추가
        const id = `push-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // interpolation 변수 추출 (data에서) - snake_case와 camelCase 모두 지원
        const interpolation: Record<string, any> = {};
        if (data) {
          if (data.serialNumber || data.serial_number) {
            interpolation.serialNumber = data.serialNumber || data.serial_number;
          }
          if (data.siteName || data.site_name) {
            interpolation.siteName = data.siteName || data.site_name;
          }
          if (data.workerName || data.worker_name) {
            interpolation.workerName = data.workerName || data.worker_name;
          }
          if (data.warningReason || data.warning_reason) {
            interpolation.warningReason = data.warningReason || data.warning_reason;
          }
          if (data.sensorName || data.sensor_name) {
            interpolation.sensorName = data.sensorName || data.sensor_name;
          }
          if (data.alarmName || data.alarm_name) {
            interpolation.alarmName = data.alarmName || data.alarm_name;
          }
          if (data.timestamp) {
            interpolation.timestamp = data.timestamp;
          }
          if (data.companyName || data.company_name) {
            interpolation.companyName = data.companyName || data.company_name;
          }
        }

        // 백엔드에서 오는 키값을 그대로 번역
        const translatedTitle = title ? String(i18n.t(title, interpolation)) : 'Notification';
        const translatedBody = body ? String(i18n.t(body, interpolation)) : 'Notification';

        const newNotification: PushNotification = {
          id,
          title: translatedTitle,
          body: translatedBody,
          data,
          timestamp: Date.now(),
          read: false,
          source,
        };

        set(state => {
          // 같은 기기(serial_number)의 기존 알림 제거
          const serialNumber = data?.serial_number;
          let filteredNotifications = state.pushNotifications;

          if (serialNumber) {
            // 같은 serial_number의 알림 제거 (기기별로 최신 하나만 유지)
            filteredNotifications = state.pushNotifications.filter(
              notif => notif.data?.serial_number !== serialNumber
            );
          }

          // 새 알림을 맨 앞에 추가하고 최대 1000개 유지
          return {
            pushNotifications: [newNotification, ...filteredNotifications].slice(0, 1000),
          };
        });
      },

      markAsRead: (id: string) => {
        // ZUSTAND: 알림 읽음 처리
        set(state => ({
          pushNotifications: state.pushNotifications.map(notif =>
            notif.id === id ? { ...notif, read: true } : notif
          ),
        }));
      },

      markAllAsRead: () => {
        // ZUSTAND: 모든 알림 읽음 처리
        set(state => ({
          pushNotifications: state.pushNotifications.map(notif => ({ ...notif, read: true })),
        }));
      },

      clearPushNotifications: () => {
        set({ pushNotifications: [] });
      },

      getUnreadCount: () => {
        return get().pushNotifications.filter(notif => !notif.read).length;
      },

      getRecentNotifications: (minutes = 10) => {
        const now = Date.now();
        const minutesAgo = minutes * 60 * 1000; // 분을 밀리초로 변환
        return get().pushNotifications.filter(notif => now - notif.timestamp <= minutesAgo);
      },

      getNotificationsWithinMinutes: (minutes: number) => {
        const now = Date.now();
        const minutesAgo = minutes * 60 * 1000; // 분을 밀리초로 변환
        return get().pushNotifications.filter(notif => now - notif.timestamp <= minutesAgo);
      },
    }),
    {
      name: 'notification-storage',
      partialize: state => ({
        pushNotifications: state.pushNotifications,
      }),
    }
  )
);

// Development helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__NOTIFICATION_STORE__ = useNotificationStore;

  (window as any).getNotificationState = () => {
    const state = useNotificationStore.getState();
    console.log('🔍 Notification Store State:', {
      totalNotifications: state.pushNotifications.length,
      unreadCount: state.getUnreadCount(),
      recentNotifications: state.pushNotifications.slice(0, 5),
    });
    return state;
  };

  (window as any).watchNotificationStore = () => {
    console.log('👀 Watching notification store changes...');
    return useNotificationStore.subscribe(state => {
      console.log('🔄 [Notification Store Update]', {
        totalCount: state.pushNotifications.length,
        unreadCount: state.getUnreadCount(),
        latestNotification: state.pushNotifications[0]?.title,
      });
    });
  };

  // console.log('🛠️ Notification Store Debug Tools Available:');
  // console.log('  - getNotificationState() : 현재 알림 상태 확인');
  // console.log('  - watchNotificationStore() : 실시간 변화 감지');
  // console.log('  - __NOTIFICATION_STORE__.getState() : 전체 상태');
}
